/**
 * Abstract base API client
 */

import { 
  ApiClientConfig, 
  RequestOptions, 
  ApiClientResponse, 
  ApiClientError,
  RetryConfig 
} from './types';

// Default retry configuration
const DEFAULT_RETRY_CONFIG: Required<RetryConfig> = {
  maxRetries: 3,
  initialDelay: 500,
  maxDelay: 1000,
  backoffFactor: 2,
  retryStatusCodes: [408, 429, 500, 502, 503, 504]
};

export abstract class BaseApiClient {
  protected config: ApiClientConfig;
  protected retryConfig: Required<RetryConfig>;

  constructor(config: ApiClientConfig) {
    this.config = config;
    this.retryConfig = { ...DEFAULT_RETRY_CONFIG, ...config.retryConfig };
  }

  // Abstract methods that must be implemented by platform-specific clients
  protected abstract makeRequest<T = any>(
    url: string, 
    options?: RequestOptions
  ): Promise<ApiClientResponse<T>>;

  // Public API methods
  async get<T = any>(url: string, options?: Omit<RequestOptions, 'method'>): Promise<T> {
    const response = await this.request<T>(url, { ...options, method: 'GET' });
    return response.data;
  }

  async post<T = any>(url: string, data?: any, options?: Omit<RequestOptions, 'method' | 'body'>): Promise<T> {
    const response = await this.request<T>(url, { 
      ...options, 
      method: 'POST', 
      body: data 
    });
    return response.data;
  }

  async put<T = any>(url: string, data?: any, options?: Omit<RequestOptions, 'method' | 'body'>): Promise<T> {
    const response = await this.request<T>(url, { 
      ...options, 
      method: 'PUT', 
      body: data 
    });
    return response.data;
  }

  async patch<T = any>(url: string, data?: any, options?: Omit<RequestOptions, 'method' | 'body'>): Promise<T> {
    const response = await this.request<T>(url, { 
      ...options, 
      method: 'PATCH', 
      body: data 
    });
    return response.data;
  }

  async delete<T = any>(url: string, options?: Omit<RequestOptions, 'method'>): Promise<T> {
    const response = await this.request<T>(url, { 
      ...options, 
      method: 'DELETE'
    });
    return response.data;
  }

  // Raw request method that returns full response
  async request<T = any>(url: string, options?: RequestOptions): Promise<ApiClientResponse<T>> {
    return this.requestWithRetry<T>(url, options);
  }

  // Internal request method with retry logic
  private async requestWithRetry<T = any>(
    url: string, 
    options?: RequestOptions, 
    attempt = 0
  ): Promise<ApiClientResponse<T>> {
    try {
      const fullUrl = this.buildUrl(url);
      const requestOptions = await this.prepareRequestOptions(options);
      
      const response = await this.makeRequest<T>(fullUrl, requestOptions);
      
      if (response.ok) {
        return response;
      }

      // Handle authentication errors
      if (response.status === 401 && this.config.authProvider) {
        console.log('🔍 BaseApiClient: 401 detected, attempting token refresh...', {
          hasAuthProvider: !!this.config.authProvider,
          responseStatus: response.status,
          url: fullUrl
        });
        try {
          await this.config.authProvider.refreshToken();
          console.log('🔍 BaseApiClient: Token refresh successful, retrying request...');
          // Retry the request with new token
          const retryOptions = await this.prepareRequestOptions(options);
          return this.makeRequest<T>(fullUrl, retryOptions);
        } catch (refreshError) {
          console.error('🔍 BaseApiClient: Token refresh failed:', refreshError);
          throw this.createError(response, 'Authentication failed');
        }
      }

      console.log('🔍 BaseApiClient: 401 auth condition not met', {
        responseStatus: response.status,
        hasAuthProvider: !!this.config.authProvider,
        url: fullUrl
      });

      throw this.createError(response, `HTTP error ${response.status}`);

    } catch (error) {
      throw error;
    }
  }

  // Helper methods
  private buildUrl(url: string): string {
    if (url.startsWith('http')) {
      return url;
    }
    
    const baseURL = this.config.baseURL.endsWith('/') 
      ? this.config.baseURL.slice(0, -1) 
      : this.config.baseURL;
    const path = url.startsWith('/') ? url : `/${url}`;
    
    return `${baseURL}${path}`;
  }

  private async prepareRequestOptions(options?: RequestOptions): Promise<RequestOptions> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...this.config.defaultHeaders,
      ...options?.headers,
    };

    // Add auth header if available
    if (this.config.authProvider && !this.config.authProvider.isLoading) {
      const token = this.config.authProvider.getToken();
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }
    }

    return {
      ...options,
      headers,
      timeout: options?.timeout || this.config.timeout,
    };
  }

  private createError(response: ApiClientResponse, message: string): ApiClientError {
    const error = new Error(message) as ApiClientError;
    error.status = response.status;
    error.statusText = response.statusText;
    error.response = response;
    return error;
  }
} 